import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { TransactionEntity } from "@modules/finances/entities/transactionEntity"
import { PaymentMethodType } from "@modules/finances/types/financesType"
import { UserCell } from "@modules/members/components/userCell"
import { HUText } from "@modules/ui/components/huText"
import TooltipIndicator from "@modules/ui/components/tooltipIndicator"
import { formatDateWithHyphen } from "@modules/utils/dateUtils"
import dayjs from "dayjs"
import { Tooltip } from "primereact/tooltip"
import { DefaultTheme } from "styled-components"

export type TransactionsColumnsType = "date" | "name" | "product" | "offer" | "price" | "fee" | "paymentMethod"
export const boxTransactionsColumns: TransactionsColumnsType[] = ["name", "offer", "price", "fee", "paymentMethod"]
export const userTransactionsColumns: TransactionsColumnsType[] = ["date", "name", "product", "price", "fee"]

export const headerColumn = (key: TransactionsColumnsType) => {
  switch (key) {
    case "date":
      return word("finances.page.table.header.date")
    case "name":
      return word("finances.page.table.header.transactions")
    case "product":
      return word("finances.page.table.header.product")
    case "offer":
      return word("finances.page.table.header.offer")
    case "price":
      return word("finances.page.table.header.price")
    case "fee":
      return (
        <HUText className="tooltip flex flex-row align-items-center cursor-pointer" color="white" fontStyle="TS">
          {word("finances.page.table.header.fee")}
          <TooltipIndicator className="ml-2" />
        </HUText>
      )
    case "paymentMethod":
      return word("finances.page.table.header.paymentMethod")
  }
}

export const valueColumn = (
  columnType: TransactionsColumnsType,
  transaction: TransactionEntity,
  theme: DefaultTheme,
  language: string,
) => {
  const colorStatus = transaction.getTransactionColorStatus(theme)
  const wordingStatus = transaction.getTransactionStatusWording()

  switch (columnType) {
    case "date":
      return (
        <HUText fontStyle="TS" style={{ whiteSpace: "nowrap" }}>
          {formatDateWithHyphen(dayjs(transaction.date).format("L"))}
        </HUText>
      )
    case "name":
      return (
        <div>
          <HUText
            className={`tooltip${transaction.transactionId} flex flex-row align-items-center cursor-pointer`}
            data-pr-showdelay={200}
            data-pr-hidedelay={100}
            data-pr-position="bottom"
            color={theme.textPrimary}
            fontStyle="TS"
          >
            {transaction.status ? (
              <Tooltip className="tooltip-container" target={`.tooltip${transaction.transactionId}`}>
                <div className="tooltip-content text-justify">
                  <HUText type="label" fontStyle="BM">
                    {wordingStatus}
                  </HUText>
                </div>
              </Tooltip>
            ) : null}

            <UserCell
              member={{
                avatar: transaction.user.avatar ?? undefined,
                username: transaction.user.firstname,
                firstname: transaction.user.firstname,
                lastname: transaction.user.lastname,
              }}
              borderColor={colorStatus}
              subtitleColor={colorStatus}
              subtitle={transaction.status !== "SUCCEEDED" || transaction.refunded ? wordingStatus : undefined}
            />
          </HUText>
        </div>
      )
    case "product":
    case "offer":
      return <HUText fontStyle="BM">{`${transaction.product.name}`}</HUText>
    case "price":
      return (
        <HUText fontStyle="BM" className="pl-1">{`${
          transaction.amount !== null ? formatCurrency(transaction.amount, transaction.currency, language) : "--"
        }`}</HUText>
      )
    case "fee":
      return (
        <HUText fontStyle="BM" className="pl-1">{`${
          transaction.fee !== null && transaction.fee !== 0
            ? formatCurrency(transaction.fee, transaction.currency, language)
            : "--"
        }`}</HUText>
      )
    case "paymentMethod":
      return (
        <HUText fontStyle="BM" className="text-center">
          {transaction.paymentMethodType === PaymentMethodType.CARD
            ? word("finances.page.table.paymentMethod.card")
            : transaction.paymentMethodType === PaymentMethodType.SEPA_DEBIT
              ? word("finances.page.table.paymentMethod.sepaDebit")
              : word("finances.page.table.paymentMethod.other")}
        </HUText>
      )
  }
}
